// src/styles/theme.js
// SooPooDing 디자인 시스템 — 공공 정보 서비스 팔레트 (ui-ux-pro-max: Government/Public Service)
// 원칙: 네이비 기반 신뢰감 / 그림자 대신 보더 / 옅은 배경+진한 텍스트 배지 / WCAG AA 이상
// 대비 기준: 일반 텍스트 4.5:1 이상만 본문에 사용

export const colors = {
  // Brand — 네이비 프라이머리 + 블루 액센트
  primary: '#0F172A',       // 헤더 로고·강조 텍스트 (흰 배경 17.9:1)
  primaryDark: '#020617',   // 최상위 제목
  accent: '#0369A1',        // CTA 버튼·링크 (흰 배경 5.6:1 / 흰 글씨 대비 5.6:1)
  accentDark: '#075985',    // 액센트 hover
  accentLight: '#E0F2FE',   // 옅은 액센트 배경 (선택 상태·배지)
  // 하위호환 별칭 (기존 코드에서 참조)
  primaryLight: '#E0F2FE',

  // Neutral — slate 스케일
  grey900: '#020617',  // 제목
  grey800: '#1E293B',  // 본문 강조
  grey700: '#334155',  // 본문 (흰 배경 10.9:1)
  grey600: '#475569',  // 보조 텍스트 (흰 배경 7.5:1 — 옅은 회색 배경 위에서도 안전)
  grey500: '#94A3B8',  // 장식·비활성 전용 (본문 금지)
  grey400: '#CBD5E1',  // 플레이스홀더
  grey300: '#CBD5E1',  // 비활성 보더
  grey200: '#E2E8F0',  // 구분선·카드 보더
  grey100: '#F1F5F9',  // 인풋 배경·칩 배경
  grey50:  '#F8FAFC',  // 페이지 배경
  white:   '#FFFFFF',

  // Semantic — 배지는 옅은 배경(Bg) + 진한 텍스트(Text) 짝으로만 사용
  red: '#DC2626',        redBg: '#FEF2F2',    redText: '#B42318',    // 마감임박·에러
  orange: '#EA580C',     orangeBg: '#FFF7ED', orangeText: '#B54708', // 줍줍·주의
  green: '#059669',      greenBg: '#ECFDF5',  greenText: '#027A48',  // 진행중·성공
  purple: '#7C3AED',     purpleBg: '#F5F3FF', purpleText: '#6941C6', // 추첨제
  blue: '#0369A1',       blueBg: '#E0F2FE',   blueText: '#075985',   // 기관·정보

  // 틴트 보더 — 옅은 배경(Bg) 상자의 테두리. Bg 보다 한 단계 진해야 상자 윤곽이 보인다.
  // 전에는 파일마다 #BFDBFE·#D3E5FF·#A4C8FF 같은 값을 따로 골랐다. 하나로 모은다.
  blueBorder:   '#BFDBFE',
  greenBorder:  '#BBF7D0',
  orangeBorder: '#FED7AA',
  redBorder:    '#FCA5A5',
};

export const radius = {
  sm: '8px',
  md: '12px',
  lg: '14px',
  xl: '16px',
  full: '999px',
};

export const spacing = {
  xs: '4px',
  sm: '8px',
  md: '12px',
  lg: '16px',
  xl: '20px',
  xxl: '24px',
  xxxl: '32px',
};

export const font = {
  // 크기 — 12px 미만 금지
  h1: '24px',
  h2: '20px',
  h3: '17px',
  body: '15px',
  small: '13px',
  caption: '12px',
  tiny: '12px',
  input: '16px',   // 16px 미만이면 iOS가 자동 확대함
  // 굵기
  bold: 700,
  semibold: 600,
  medium: 500,
  regular: 400,
};

// 카드: 그림자 대신 1px 보더 (공공 정보 서비스 톤)
export const card = {
  background: colors.white,
  borderRadius: radius.xl,
  border: `1px solid ${colors.grey200}`,
  padding: spacing.xl,
};

// 카드 전체를 감싸는 링크에 입히는 스타일
export const cardLink = {
  display: 'block',
  textDecoration: 'none',
  color: 'inherit',
};

// 배지: 옅은 배경 + 진한 텍스트 (예: badge(colors.greenText, colors.greenBg))
export const badge = (color, bg) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '3px',
  padding: '4px 9px',
  borderRadius: radius.sm,
  fontSize: font.caption,
  fontWeight: font.semibold,
  color,
  background: bg,
  lineHeight: 1,
});

// 칩: 기본 회색, 선택 시 네이비 배경 + 흰 텍스트
export const chip = (active) => ({
  flexShrink: 0,
  padding: '10px 14px',
  borderRadius: radius.full,
  border: `1px solid ${active ? colors.primary : colors.grey200}`,
  fontSize: font.small,
  fontWeight: active ? font.bold : font.medium,
  color: active ? colors.white : colors.grey700,
  background: active ? colors.primary : colors.white,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  transition: 'background 0.15s, color 0.15s, border-color 0.15s',
});

export const buttonPrimary = {
  width: '100%',
  padding: '16px',
  background: colors.accent,
  color: colors.white,
  border: 'none',
  borderRadius: radius.lg,
  fontSize: font.body,
  fontWeight: font.bold,
  cursor: 'pointer',
  transition: 'background 0.15s',
};

export const buttonSecondary = {
  width: '100%',
  padding: '14px',
  background: colors.white,
  color: colors.grey800,
  border: `1px solid ${colors.grey200}`,
  borderRadius: radius.lg,
  fontSize: font.body,
  fontWeight: font.semibold,
  cursor: 'pointer',
};
